<template>
  <div class="d-flex flex-column">
    <div class="d-flex align-center w-100 ga-4 justify-space-between mb-3 flex-column flex-sm-row">
      <div v-if="displaySiteSelector" class="d-flex align-center flex ga-4 w-100 w-sm-auto">
        <SiteSelector
          v-if="displaySiteSelector"
          v-model="selectedSiteId"
          style="min-width: 250px"
          hide-details
        />
        <div>
          <VueDatePicker
            v-model="dates"
            :locale="locale"
            :cancel-text="t('form.cancel')"
            :select-text="t('form.select')"
            format="dd/MM/yyyy"
            :placeholder="t('components.transactions.filters.dateRange.label')"
            :range="{ noDisabledRange: true }"
            :max-date="new Date()"
            :enable-time-picker="false"
            :preset-dates="datePickerPresetRanges"
            position="right"
            class="d-flex"
          >
            <template #trigger>
              <y-btn icon variant="text">
                <v-icon>mdi-calendar</v-icon>
              </y-btn>
            </template>
          </VueDatePicker>
        </div>
      </div>

      <div class="d-flex align-center justify-space-between w-100 w-sm-auto">
        <VueDatePicker
          v-if="!displaySiteSelector"
          v-model="dates"
          :locale="locale"
          :cancel-text="t('form.cancel')"
          :select-text="t('form.select')"
          format="dd/MM/yyyy"
          :placeholder="t('components.transactions.filters.dateRange.label')"
          :range="{ noDisabledRange: true }"
          :max-date="new Date()"
          :enable-time-picker="false"
          :preset-dates="datePickerPresetRanges"
          position="right"
          class="d-inline-block d-sm-none"
        >
          <template #trigger>
            <y-btn icon variant="text">
              <v-icon>mdi-calendar</v-icon>
            </y-btn>
          </template>
        </VueDatePicker>

        <y-btn-nd class="me-2" width="50px" variant="text" @click="refresh">
          <v-icon>mdi-refresh</v-icon>
        </y-btn-nd>
        <y-btn
          class="flex-grow-1"
          :disabled="!selectedSiteId || isEODReportRunning"
          :loading="isEODReportRunning"
          @click="createReport"
        >
          {{ t("eod-reports.createReport") }}
        </y-btn>
      </div>
    </div>

    <div class="d-flex w-100 mb-2">
      <v-chip
        v-if="dates.length > 0"
        closable
        :text="formatDateFilterText"
        color="purple-4"
        variant="outlined"
        :size="chipSize"
        inline
        @click:close="clearDates"
      />
    </div>

    <v-infinite-scroll
      :key="infiniteScrollKey"
      :items="reportsByMonth"
      color="primary"
      @load="load"
    >
      <template v-for="[month, items] in Object.entries(reportsByMonth)" :key="month">
        <v-divider class="my-2" />
        <div class="text-uppercase text-subtitle-2 font-weight-bold my-2">
          {{ month }}
        </div>
        <template v-for="(item, index) in items" :key="index">
          <div class="text-uppercase text-subtitle-2 font-weight-light text-grey-darken-1 my-2">
            {{ formatDay(item.to) }}
          </div>
          <v-card
            v-if="!expandedReports.has(`${month}-${index}`)"
            class="my-2 px-5 d-flex flex-column py-8 rounded-xl"
            elevation="1"
            @click="toggleExpanded(`${month}-${index}`)"
          >
            <div class="d-flex align-center">
              <div class="font-weight-bold">
                {{ displayAmount(item.totalTrx!) }}
              </div>
              <v-spacer />
              <div class="d-flex flex-column justify-center">
                <v-icon class="text-grey-darken-1 ms-2"> mdi-arrow-down </v-icon>
              </div>
            </div>
          </v-card>

          <v-card v-else class="my-2 px-5 d-flex flex-column py-8 rounded-xl" elevation="1">
            <!-- Header -->
            <div class="d-flex justify-space-between align-center mb-4">
              <div class="text-h6 font-weight-bold">{{ t("eod-reports.totals") }}</div>
              <div class="text-caption text-grey-darken-1">
                {{ formatRange(item.from, item.to) }}
                <v-icon
                  class="text-grey-darken-1 ms-2"
                  @click="toggleExpanded(`${month}-${index}`)"
                >
                  mdi-close
                </v-icon>
              </div>
            </div>

            <!-- GBP Total Row -->
            <div
              class="d-flex justify-space-between align-center py-3"
              @click="toggleExpanded(`${month}-${index}-totals`)"
            >
              <div class="d-flex align-center">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <span class="text-h6 font-weight-bold me-2">∑</span>
                <span class="font-weight-bold">{{ item.totalTrx.currency }}</span>
              </div>
              <div class="d-flex align-center">
                <span class="text-h6 font-weight-bold me-1">{{ total(item) }}</span>
                <v-icon size="16"
                  >mdi-arrow-{{
                    // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
                    expandedReports.has(`${month}-${index}-totals`) ? "up" : "down"
                  }}</v-icon
                >
              </div>
            </div>

            <!-- Breakdown Table -->
            <v-table v-if="expandedReports.has(`${month}-${index}-totals`)" class="mb-4">
              <thead>
                <tr class="text-caption text-grey-darken-1">
                  <th class="text-left">{{ t("eod-reports.type") }}</th>
                  <th class="text-right">#</th>
                  <th class="text-right">{{ item.totalTrx.currency }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ t("eod-reports.trx") }}</td>
                  <td class="text-right">{{ item.trxCount }}</td>
                  <td class="text-right">{{ displayAmount(item.totalTrx!) }}</td>
                </tr>
                <tr>
                  <td>{{ t("eod-reports.tips") }}</td>
                  <td class="text-right">{{ item.tipsCount }}</td>
                  <td class="text-right">{{ displayAmount(item.totalTips!) }}</td>
                </tr>
                <tr>
                  <td>{{ t("eod-reports.refunds") }}</td>
                  <td class="text-right">{{ item.refundsCount }}</td>
                  <td class="text-right">{{ displayAmount(item.totalRefunds) }}</td>
                </tr>
                <tr>
                  <td>{{ t("eod-reports.total") }}</td>
                  <td class="text-right"></td>
                  <td class="text-right">{{ total(item) }}</td>
                </tr>
              </tbody>
            </v-table>

            <v-divider />

            <div v-for="(pm, scheme) in item.totalPaymentMethod" :key="scheme">
              <div
                class="d-flex justify-space-between align-center py-6"
                @click="toggleExpanded(`${month}-${index}-${scheme}`)"
              >
                <div class="d-flex align-center">
                  <payment-method :method="scheme" />
                  <span class="text-uppercase font-weight-bold">{{ scheme }}</span>
                </div>
                <div class="d-flex align-center">
                  <span class="font-weight-medium me-1">{{ total(pm) }}</span>
                  <v-icon size="16"
                    >mdi-arrow-{{
                      // eslint-disable-next-line @intlify/vue-i18n/no-raw-text
                      expandedReports.has(`${month}-${index}-${scheme}`) ? "up" : "down"
                    }}</v-icon
                  >
                </div>
              </div>

              <v-table v-if="expandedReports.has(`${month}-${index}-${scheme}`)" class="mb-4">
                <thead>
                  <tr class="text-caption text-grey-darken-1">
                    <th class="text-left">{{ t("eod-reports.type") }}</th>
                    <th class="text-right">#</th>
                    <th class="text-right">{{ pm.totalTrx.currency }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{{ t("eod-reports.trx") }}</td>
                    <td class="text-right">{{ pm.trxCount }}</td>
                    <td class="text-right">{{ displayAmount(pm.totalTrx) }}</td>
                  </tr>
                  <tr>
                    <td>{{ t("eod-reports.tips") }}</td>
                    <td class="text-right">{{ pm.tipsCount }}</td>
                    <td class="text-right">{{ displayAmount(pm.totalTips) }}</td>
                  </tr>
                  <tr>
                    <td>{{ t("eod-reports.refunds") }}</td>
                    <td class="text-right">{{ pm.refundsCount }}</td>
                    <td class="text-right">{{ displayAmount(pm.totalRefunds) }}</td>
                  </tr>
                  <tr>
                    <td>{{ t("eod-reports.total") }}</td>
                    <td class="text-right"></td>
                    <td class="text-right">{{ total(pm) }}</td>
                  </tr>
                </tbody>
              </v-table>

              <v-divider />
            </div>

            <v-card-actions v-if="isDevice()" class="pt-5">
              <y-btn class="flex-grow-1" @click="printReport(item)">{{
                t("eod-reports.print")
              }}</y-btn>
            </v-card-actions>
          </v-card>
        </template>
      </template>
      <template #empty>
        <span v-if="(Object.keys(reportsByMonth)?.length ?? 0) > 0 && !lastKey">{{
          t("eod-reports.noMoreReports")
        }}</span>
        <span v-else-if="(Object.keys(reportsByMonth)?.length ?? -1) === 0">
          {{ t("eod-reports.noReports") }}
        </span>
      </template>
    </v-infinite-scroll>
  </div>
</template>

<script setup lang="ts">
import { Amount, EODReport } from "@tableyeti/merchant-service";
import { AxiosError, isAxiosError } from "axios";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useApplicationStore } from "@/store/application";
import { displayAmount } from "@/utils/format";

const emit = defineEmits<{
  (e: "mount"): void;
  (e: "print", receipt: EODReportReceipt): void;
}>();

const { siteId } = defineProps({
  displaySiteSelector: {
    type: Boolean,
    required: false,
    default: true,
  },
  siteId: {
    type: String,
    required: false,
    default: undefined,
  },
});

// print EOD report data
interface BreakdownItem {
  name: string;
  amount: Amount;
  count: number;
  inclusive?: boolean;
}

interface Breakdown {
  name: string;
  totalAmount: Amount;
  totalCount: number;
  breakdownItems: BreakdownItem[];
}

interface Totals {
  name: string;
  totalAmount: Amount;
  totalCount: number;
  breakdownItems: BreakdownItem[];
}

interface EODReportReceipt {
  from: string;
  to: string;
  totals: Totals;
  breakdowns: Breakdown[];
}

const { api, currentMerchantId } = useYetipayApi();
const applicationStore = useApplicationStore();
const { xs } = useDisplay();
const { t, locale } = useI18n();
const startOfDayToday = startOfDay(new Date());
const endOfDayToday = endOfDay(new Date());
// TODO(stefano-yeti): move to a util and refactor i18n keys
const datePickerPresetRanges = ref([
  {
    label: t("components.transactions.filters.dateRange.presets.today"),
    value: [startOfDayToday, endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.yesterday"),
    value: [subDays(startOfDayToday, 1), subDays(endOfDayToday, 1)],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisWeek"),
    value: [startOfWeek(startOfDayToday, { weekStartsOn: 1 }), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.lastWeek"),
    value: [
      startOfWeek(subWeeks(startOfDayToday, 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(endOfDayToday, 1), { weekStartsOn: 1 }),
    ],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisMonth"),
    value: [startOfMonth(startOfDayToday), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.lastMonth"),
    value: [startOfMonth(subMonths(startOfDayToday, 1)), endOfMonth(subMonths(endOfDayToday, 1))],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.last90Days"),
    value: [subDays(startOfDayToday, 90), endOfDayToday],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.last6Months"),
    value: [startOfMonth(subMonths(startOfDayToday, 6)), endOfMonth(subMonths(endOfDayToday, 1))],
  },
  {
    label: t("components.transactions.filters.dateRange.presets.thisYear"),
    value: [startOfYear(startOfDayToday), endOfDayToday],
  },
]);

// VInfiniteScroll has internal state that needs to be reset when the type changes or during refresh
const infiniteScrollKey = ref(Math.random());
const reportsByMonth = ref<Record<string, EODReport[]>>({});
const lastKey = ref<string | undefined>(undefined);
const selectedSiteId = ref<string | undefined>(siteId);
const dates = ref<Date[]>([]);
const isEODReportRunning = ref(false);

const expandedReports = ref<Set<string>>(new Set());

onMounted(async () => {
  emit("mount");
});

const toggleExpanded = (index: string) => {
  if (expandedReports.value.has(index)) {
    expandedReports.value.delete(index);
  } else {
    expandedReports.value.add(index);
  }
};

//TODO(stefano-yeti): remove this once we have a way to check if the device is a terminal
const isDevice = () => {
  return false;
};

const total = (item: { totalTrx: Amount; totalTips: Amount; totalRefunds: Amount }) => {
  const totalTrx = item.totalTrx;
  const totalTips = item.totalTips;
  const totalRefunds = item.totalRefunds;

  const total =
    (totalTrx ? totalTrx.value : 0) -
    (totalTips ? totalTips.value : 0) -
    (totalRefunds ? totalRefunds.value : 0);
  return displayAmount({
    value: total,
    currency: totalTrx ? totalTrx.currency : "",
  });
};

const formatTime = (isoDate: string) => {
  const date = new Date(isoDate);

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;

  return `${hour12} : ${minutes} ${ampm}`;
};

const formatRange = (isoDateFrom: string, isoDateTo: string) => {
  return `[${formatTime(isoDateFrom)} - ${formatTime(isoDateTo)}]`;
};

const formatDay = (isoDate: string) => {
  const date = new Date(isoDate);

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString(locale.value, { month: "short" }).toUpperCase();
  const year = date.getFullYear();

  return `${day} ${month}, ${year} [${formatTime(isoDate)}]`;
};

const byMonth = (items: EODReport[]) => {
  for (const item of items) {
    const to = new Date(item.to);

    const monthKey = to.toLocaleDateString(locale.value, {
      month: "long",
      year: "numeric",
    });
    if (!reportsByMonth.value[monthKey]) {
      reportsByMonth.value[monthKey] = [];
    }
    reportsByMonth.value[monthKey].push(item);
  }
};

const chipSize = computed(() => (xs.value ? "small" : "default"));

watch(selectedSiteId, () => {
  reportsByMonth.value = {};
  lastKey.value = undefined;
  infiniteScrollKey.value = Math.random();
});

watch(dates, () => {
  reportsByMonth.value = {};
  lastKey.value = undefined;
  infiniteScrollKey.value = Math.random();
});

const load = async (load: {
  side: string;
  done: (status: "loading" | "error" | "empty" | "ok") => void;
}) => {
  try {
    // No more results to load
    if (lastKey.value === "") {
      load.done("empty");
      return;
    }

    const datesRange =
      dates.value.length > 0 ? dates.value.map((d) => d.toISOString()).join(",") : undefined;

    const response = await api.listEODReports(
      currentMerchantId,
      selectedSiteId.value,
      lastKey.value,
      datesRange,
    );
    const items = response.data.data || [];

    byMonth(items);

    // If lastKey is missing, this is the last page
    if (response.data.lastKey) {
      lastKey.value = response.data.lastKey;
    } else {
      // Mark the end of pagination by setting a sentinel
      lastKey.value = "";
    }

    load.done("ok");
  } catch (e) {
    console.error(e);
    load.done("error");
  }
};

const refresh = async () => {
  reportsByMonth.value = {};
  lastKey.value = undefined;
  infiniteScrollKey.value = Math.random();
};

const formatDateFilterText = computed(() => {
  if (dates.value.length === 0) {
    return "";
  }

  return `${dates.value[0].toLocaleDateString(locale.value)} - ${dates.value[1].toLocaleDateString(locale.value)}`;
});

const clearDates = () => {
  dates.value = [];
};

const isReportGenerated = async (from?: string, to?: string) => {
  if (!from || !to) {
    return false;
  }

  const maxTimeout = 60000; // 1 minute
  const start = Date.now();
  let delay = 500; // start with 500ms
  while (Date.now() - start < maxTimeout) {
    try {
      const response = await api.listEODReports(currentMerchantId, selectedSiteId.value);
      const reports: EODReport[] = response.data.data || [];
      const found = reports.find((r) => r.from === from && r.to === to);
      if (found) {
        return true;
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
    await new Promise((resolve) => setTimeout(resolve, delay));
    delay *= 2;
  }
  return false;
};

const createReport = async () => {
  if (!selectedSiteId.value) {
    return;
  }
  try {
    isEODReportRunning.value = true;
    const res = await api.createEODReport(currentMerchantId, {
      siteId: selectedSiteId.value,
    });
    const { from, to } = res.data;
    const done = await isReportGenerated(from, to);
    if (done) {
      applicationStore.notifyUser({
        message: t("eod-reports.reportCreated"),
        type: "success",
      });
      refresh();
    }
  } catch (error) {
    if (isAxiosError(error)) {
      const axiosError = error as AxiosError;

      //a report is beeing created already
      if (axiosError.response?.status === 409) {
        applicationStore.notifyUser({
          message: t("eod-reports.reportAlreadyRunning"),
          type: "warning",
        });
        return;
      }

      //no new transactions for report
      if (axiosError.response?.status === 422) {
        //no permission to create report
        applicationStore.notifyUser({
          message: t("eod-reports.noNewTransactions"),
          type: "warning",
        });
        return;
      }
    }
    console.error(error);
    applicationStore.notifyUser({
      message: t("eod-reports.genericError"),
      type: "error",
    });
  } finally {
    isEODReportRunning.value = false;
  }
};

const mapEODReportToEODReportReceipt = (report: EODReport): EODReportReceipt => {
  // Create breakdown items for totals
  const totalBreakdownItems: BreakdownItem[] = [
    {
      name: t("eod-reports.trx"),
      amount: report.totalTrx,
      count: report.trxCount,
    },
    {
      name: t("eod-reports.tips"),
      amount: report.totalTips,
      count: report.tipsCount,
    },
    {
      name: t("eod-reports.refunds"),
      amount: report.totalRefunds,
      count: report.refundsCount,
    },
  ];

  // Create breakdowns for each payment method
  const breakdowns: Breakdown[] = Object.entries(report.totalPaymentMethod)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([name, value]) => ({
      name,
      totalAmount: {
        currency: value.totalTrx.currency,
        value: value.totalTrx.value - value.totalTips.value - value.totalRefunds.value,
      },
      totalCount: value.trxCount,
      breakdownItems: [
        {
          name: t("eod-reports.trx"),
          amount: value.totalTrx,
          count: value.trxCount,
        },
        {
          name: t("eod-reports.tips"),
          amount: value.totalTips,
          count: value.tipsCount,
        },
        {
          name: t("eod-reports.refunds"),
          amount: value.totalRefunds,
          count: value.refundsCount,
        },
      ],
    }));

  return {
    from: report.from,
    to: report.to,
    totals: {
      name: "",
      totalAmount: {
        currency: report.totalTrx.currency,
        value: report.totalTrx.value - report.totalTips.value - report.totalRefunds.value,
      },
      totalCount: report.trxCount,
      breakdownItems: totalBreakdownItems,
    },
    breakdowns,
  };
};

const printReport = (eodReport: EODReport) => {
  const receipt = mapEODReportToEODReportReceipt(eodReport);
  emit("print", receipt);
};
</script>

<style scoped>
.v-table .v-table__wrapper > table > thead > tr > th {
  border-bottom: none;
}

.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > td,
.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > th {
  border-bottom: none;
}
</style>
